import { useCallback, useState } from 'react';
import type { Account, Folder } from '@/api';
import { useAccountContext } from '@/app/accounts';
import { FolderProvider, useFolderContext } from '@/app/folders';
import { AccountStatusDot, needsPassword, ReenterPasswordLink } from './AccountSwitcher';
import { useReauthPrompt } from '@/features/credential-notice/reauthPrompt';
import { LabelsSection } from '@/features/labels/LabelsSection';
import { FolderTree } from './FolderTree';
import { Icon } from './Icon';
import { useHoverToOpen } from '@/features/dnd/useFolderDrop';
import { FolderTreeSkeleton } from './Skeleton';
import { UnifiedFolderEntry } from './UnifiedFolderEntry';
import { needsAgainText } from '@/features/oauth-connect/oauth';

/**
 * The sidebar's navigation tree: every mailbox, each opening onto its own
 * folders, with "All inboxes" above them.
 *
 * ADR 0056 put "All inboxes" above the *active* account's folder tree, which
 * left the other mailboxes reachable only through the account switcher — a
 * mode change with no visible trace, and one that had nothing to do with the
 * list the user was looking at. The owner asked for the mailboxes to be listed
 * under the unified entry, each expandable to its own folders, and this is that.
 *
 * **One shape, in and out of the unified view.** Two shapes would mean the
 * sidebar rearranges itself as a side effect of picking a list, and the
 * question "where are that mailbox's folders?" would have two different
 * answers depending on where you happened to be standing. Outside the unified
 * view the tree is identical; only the highlighted row differs. With exactly
 * ONE mailbox — most people — nothing changes at all: no unified entry, no
 * mailbox row, just the folder tree that was always there.
 *
 * Picking a folder inside a mailbox makes that mailbox the active one, because
 * a folder id means nothing without an account (ADR 0012) and the mail view
 * resolves the one in the URL against the active mailbox's tree.
 */

/** Expanded mailboxes, for this tab only: a sidebar is not a preference. */
const EXPANDED_KEY = 'ols-mail.sidebar-mailboxes';

function readExpanded(): string[] | undefined {
  try {
    const raw = sessionStorage.getItem(EXPANDED_KEY);
    if (raw === null) return undefined;
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : undefined;
  } catch {
    return undefined;
  }
}

function writeExpanded(ids: ReadonlySet<string>): void {
  try {
    sessionStorage.setItem(EXPANDED_KEY, JSON.stringify([...ids]));
  } catch {
    /* storage blocked — the sidebar just forgets when the tab reloads */
  }
}

export interface MailboxTreeProps {
  /** The open folder, highlighted only inside the mailbox it belongs to. */
  selectedFolderId: string | undefined;
  /** Whether the unified list is the one on screen. */
  unifiedSelected: boolean;
  onSelectUnified: () => void;
  /**
   * A folder was picked. The mailbox it belongs to has already been made the
   * active one; the caller only has to navigate.
   */
  onSelectFolder: (folder: Folder) => void;
}

export function MailboxTree({
  selectedFolderId,
  unifiedSelected,
  onSelectUnified,
  onSelectFolder,
}: MailboxTreeProps) {
  const { accounts, activeAccountId, setActiveAccountId } = useAccountContext();
  const promptReauth = useReauthPrompt();

  /*
   * Which mailboxes are open. `undefined` until the first toggle *and* nothing
   * in session storage: the default is "the one you are in", which is not a
   * set we want to write down — it would freeze today's active mailbox into a
   * choice the user never made.
   */
  const [expanded, setExpanded] = useState<Set<string> | undefined>(() => {
    const stored = readExpanded();
    return stored ? new Set(stored) : undefined;
  });

  const isExpanded = (accountId: string): boolean =>
    expanded ? expanded.has(accountId) : accountId === activeAccountId;

  const toggle = useCallback(
    (accountId: string) => {
      setExpanded((previous) => {
        const base = previous ?? new Set(activeAccountId !== undefined ? [activeAccountId] : []);
        const next = new Set(base);
        if (next.has(accountId)) next.delete(accountId);
        else next.add(accountId);
        writeExpanded(next);
        return next;
      });
    },
    [activeAccountId],
  );

  const selectFolder = useCallback(
    (folder: Folder) => {
      // The account first: the mail view resolves the folder id in the URL
      // against the active mailbox's tree, so the two have to move together.
      if (folder.accountId !== activeAccountId) setActiveAccountId(folder.accountId);
      onSelectFolder(folder);
    },
    [activeAccountId, onSelectFolder, setActiveAccountId],
  );

  // One mailbox: exactly the tree this sidebar has always drawn. There is
  // nothing to unify and nothing to choose between, so neither row is drawn.
  if (accounts.length < 2) {
    return <ActiveMailboxFolders selectedFolderId={selectedFolderId} onSelect={selectFolder} />;
  }

  return (
    // `data-pane`: the folders pane for F6 (#205), as FolderTree's root is
    // with one mailbox.
    <div
      className="folder-tree mailbox-tree"
      role="tree"
      aria-label="Mailboxes and folders"
      data-pane="folders"
      tabIndex={-1}
    >
      <UnifiedFolderEntry selected={unifiedSelected} onSelect={onSelectUnified} />
      {accounts.map((account) => (
        <MailboxBranch
          key={account.id}
          account={account}
          expanded={isExpanded(account.id)}
          onToggle={() => {
            // Opening a mailbox whose password was rejected is the intent the
            // re-auth modal waits for (#192); its branch says the same below.
            if (!isExpanded(account.id) && !account.isPrimary && needsPassword(account)) {
              promptReauth?.(account.id);
            }
            toggle(account.id);
          }}
          // A folder id is only unique within its account (ADR 0012): `INBOX`
          // encodes to the same string in every mailbox, so highlighting by id
          // alone would light up the same row in all of them.
          selectedFolderId={account.id === activeAccountId ? selectedFolderId : undefined}
          onSelect={selectFolder}
        />
      ))}
    </div>
  );
}

/** The single-mailbox sidebar: the active account's tree, and nothing else. */
function ActiveMailboxFolders({
  selectedFolderId,
  onSelect,
}: {
  selectedFolderId: string | undefined;
  onSelect: (folder: Folder) => void;
}) {
  const { folders, isLoading } = useFolderContext();
  const { activeAccountId } = useAccountContext();
  if (isLoading) return <FolderTreeSkeleton />;
  return (
    <>
      <FolderTree folders={folders} selectedFolderId={selectedFolderId} onSelect={onSelect} />
      {/* The mailbox's labels (plan/17 §5), under its folders. */}
      {activeAccountId !== undefined && <LabelsSection accountId={activeAccountId} />}
    </>
  );
}

/**
 * One mailbox: a row that opens onto its own folders.
 *
 * The folders are fetched when the branch is first opened, not before — the
 * sidebar of someone with six mailboxes should not cost six `LIST`s on every
 * cold load to draw six collapsed rows.
 */
function MailboxBranch({
  account,
  expanded,
  onToggle,
  selectedFolderId,
  onSelect,
}: {
  account: Account;
  expanded: boolean;
  onToggle: () => void;
  selectedFolderId: string | undefined;
  onSelect: (folder: Folder) => void;
}) {
  const name = account.displayName.trim();
  // A dragged message resting on a closed mailbox opens it (#168), so a row
  // in the unified list can reach its own mailbox's folders.
  const hoverOpen = useHoverToOpen(!expanded, onToggle);
  return (
    <div
      role="treeitem"
      // Named for the mailbox (#205): from its content the name was every
      // folder, label and button nested under it, read as one sentence.
      aria-label={name !== '' ? `${name}, ${account.email}` : account.email}
      aria-expanded={expanded}
      aria-level={1}
      aria-selected={false}
    >
      <div className="folder-tree__row mailbox-row" {...hoverOpen}>
        <button
          type="button"
          className="folder-tree__twisty"
          aria-expanded={expanded}
          aria-label={expanded ? `Collapse ${account.email}` : `Expand ${account.email}`}
          onClick={onToggle}
        >
          <Icon name="chevron-right" size={14} />
        </button>
        {/* The whole row opens the mailbox too: a 1.125rem twisty is not a
            touch target, and the row has no list of its own to go to. */}
        <button type="button" className="folder-tree__main mailbox-row__main" onClick={onToggle}>
          {/* Name above address, never beside it — the same pairing the
              account switcher and the Accounts rows use. */}
          <span className="account-switcher__meta">
            <span className="account-switcher__name truncate">{name || account.email}</span>
            {name !== '' && <span className="account-switcher__email truncate">{account.email}</span>}
          </span>
          <AccountStatusDot account={account} />
        </button>
      </div>
      {expanded && (
        <FolderProvider accountId={account.id}>
          <MailboxFolders account={account} selectedFolderId={selectedFolderId} onSelect={onSelect} />
        </FolderProvider>
      )}
    </div>
  );
}

/**
 * The folders of the mailbox whose provider this is.
 *
 * A mailbox that would not answer is **named** here rather than drawn as an
 * empty branch: with several mailboxes on screen, "no folders" and "this one
 * is down" look identical, and only one of them is worth a retry (ADR 0056 §3
 * makes the same point about the merged list).
 */
function MailboxFolders({
  account,
  selectedFolderId,
  onSelect,
}: {
  account: Account;
  selectedFolderId: string | undefined;
  onSelect: (folder: Folder) => void;
}) {
  const { folders, isLoading, error, failureCount, reload } = useFolderContext();

  // The server will not retry a password the host rejected, so "Retry" here
  // could only ever fail again; the way forward is typing the password.
  if (needsPassword(account)) {
    return (
      <div className="folder-tree__status mailbox-row__status" role="alert">
        <p className="muted">{`${account.displayName.trim() || account.email} ${needsAgainText(account)}.`}</p>
        <ReenterPasswordLink account={account} className="btn btn--ghost" />
      </div>
    );
  }

  // From the second failure on, without waiting for the retry ladder to run
  // out: a branch that skeletons for a minute says nothing at all.
  if (error !== undefined || failureCount >= 2) {
    return (
      <div className="folder-tree__status mailbox-row__status" role="alert">
        <p className="muted">{`${account.displayName.trim() || account.email} couldn’t load its folders.`}</p>
        <button type="button" className="btn btn--ghost" onClick={reload}>
          Retry
        </button>
      </div>
    );
  }
  if (isLoading) return <FolderTreeSkeleton />;
  return (
    <>
      <FolderTree
        folders={folders}
        selectedFolderId={selectedFolderId}
        onSelect={onSelect}
        nested
        depthOffset={1}
      />
      {/* This mailbox's labels, never merged with another's (plan/17 §1). */}
      <LabelsSection accountId={account.id} nested />
    </>
  );
}
